import { useTranslation } from '@/lib';
import { DeviceModel } from '@/modules/devices/types/DeviceModel';
import { Circle } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import { FunctionComponent } from 'react';
import dayjs from 'dayjs';
import { DeviceType } from '@/modules/devices/api/device/device.contracts';

export const DeviceStatusCellRenderer: FunctionComponent<ICellRendererParams<DeviceModel>> = (props: ICellRendererParams<DeviceModel>) => {
  const { t } = useTranslation();
  const device = props.data;

  if (!device) {
    return null;
  }

  // Determine if device is "alive" based on the criteria:
  // - Green if last seen within 20 minutes AND offlineTimestamp is not null
  // - Red otherwise
  const now = dayjs();
  const lastSeen = device.lastEventReceivedAt ? dayjs(device.lastEventReceivedAt) : null;
  const onlineSince = device.onlineSince ? dayjs(device.onlineSince) : null;
  const isWithin20Minutes = lastSeen ? now.diff(lastSeen, 'minute') <= (device.maxTimeNotSeenMinutes ?? 20) : false;

  const isAlive = isWithin20Minutes && !device.offlineTimestamp;
  const statusColor = isAlive ? 'success' : 'error';

  // Build tooltip text with last seen and online since information
  let tooltipText = '';

  if (onlineSince && isAlive) {
    tooltipText = `${t('online_since')}: ${onlineSince.format('MMM D, YYYY HH:mm')}`;
  } else if (device.offlineTimestamp) {
    const offlineSince = dayjs(device.offlineTimestamp);
    tooltipText = `${t('offline_since')}: ${offlineSince.format('MMM D, YYYY HH:mm')}`;
  }

  return (
    <Tooltip title={tooltipText} placement="left">
      <div className="flex h-full items-center w-full">
        {device.deviceType === DeviceType.BLE_GATEWAY && <Circle fontSize="small" color={statusColor} sx={{ fontSize: '12px' }} />}
      </div>
    </Tooltip>
  );
};
